// The colour mode: follow the system, or always light, or always dark. A single button in the
// header cycles through the three, and the choice is remembered in this browser only.
//
// The mode is first applied by the script in the layout's head, before the stylesheet draws
// anything, so that a dark page is not shown light for a moment on every load — it reads the
// same key and sets the same two attributes, and has to be kept in step with this file. From
// then on this module takes over: the button, the system changing its mind while the page is
// open, and other tabs switching the mode.
//
// Every access to localStorage is wrapped: it throws in a private window with site data
// blocked, and the page then simply follows the system.

export const KEY = 'colorMode'
export const MODES = ['system', 'light', 'dark']
const DARK = '(prefers-color-scheme: dark)'

export function readMode() {
  try {
    const mode = window.localStorage.getItem(KEY)
    return MODES.includes(mode) ? mode : 'system'
  } catch {
    return 'system'
  }
}

function writeMode(mode) {
  try {
    if (mode === 'system') window.localStorage.removeItem(KEY)
    else window.localStorage.setItem(KEY, mode)
  } catch {
    // A full or blocked store costs the preference, nothing else.
  }
}

export function nextMode(mode) {
  return MODES[(MODES.indexOf(mode) + 1) % MODES.length]
}

// The theme Bootstrap is to draw: the mode itself, unless that is to follow the system.
export function themeOf(mode) {
  if (mode !== 'system') return mode
  return window.matchMedia?.(DARK).matches ? 'dark' : 'light'
}

// `data-color-mode` is what was chosen, which picks the icon of the button (see
// application.scss); `data-bs-theme` is what Bootstrap draws.
export function applyMode(mode) {
  const root = document.documentElement
  root.dataset.colorMode = mode
  root.dataset.bsTheme = themeOf(mode)
}

export function initColorMode() {
  const toggle = document.querySelector('[data-color-mode-toggle]')
  let mode = readMode()

  function sync() {
    applyMode(mode)
    if (!toggle) return

    const label = toggle.dataset[`label${mode[0].toUpperCase()}${mode.slice(1)}`]
    if (!label) return
    toggle.title = label
    toggle.setAttribute('aria-label', label)
  }

  toggle?.addEventListener('click', (event) => {
    event.preventDefault()
    mode = nextMode(mode)
    writeMode(mode)
    sync()
  })

  window.matchMedia?.(DARK).addEventListener?.('change', () => {
    if (mode === 'system') sync()
  })

  window.addEventListener('storage', (event) => {
    if (event.key !== KEY && event.key !== null) return
    mode = readMode()
    sync()
  })

  sync()
}
